import { Component } from '@theme/component';
import { setHeaderMenuStyle, lockScroll, unlockScroll } from '@theme/utilities';

class HeaderMenu extends Component {
  requiredRefs = ['overflowMenu'];
  #activeKey = null;
  #lastTrigger = null;
  #subKey = null;
  #subAnimation;
  #resizeObserver;
  #styleObserver;
  #frame = null;
  #hoverTimer;
  #animation;
  #backdropAnimation;
  #panelAnimations = [];
  #closing = false;
  #reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');

  get secondary() { return this.querySelector('[data-aupiel-secondary]'); }
  get drawer() { return this.querySelector('[data-aupiel-drawer]'); }
  get backdrop() { return this.querySelector('[data-aupiel-backdrop]'); }
  get header() { return this.closest('header-component'); }
  get duration() {
    if (this.dataset.animations !== 'true' || this.#reducedMotion.matches) return 0;
    return Number(this.dataset.duration) || 400;
  }

  connectedCallback() {
    super.connectedCallback();
    this.addEventListener('pointerover', this.#onPointerOver);
    this.addEventListener('pointerout', this.#onPointerOut);
    this.addEventListener('click', this.#onClick);
    this.addEventListener('keydown', this.#onKeydown);
    this.addEventListener('toggle', this.#onToggle, true);
    document.addEventListener('keydown', this.#onEscape);
    document.addEventListener('scroll', this.#queuePosition, true);
    window.addEventListener('resize', this.#onResize);
    this.#reducedMotion.addEventListener('change', this.#onMotionChange);
    this.#resizeObserver = new ResizeObserver(this.#queuePosition);
    if (this.header) {
      this.#resizeObserver.observe(this.header);
      for (const element of this.header.querySelectorAll('.header-logo, .aupiel-nav-list > li')) this.#resizeObserver.observe(element);
      this.#styleObserver = new MutationObserver(() => {
        if (this.header?.dataset.menuStyle === 'drawer') this.close(false, true);
        this.#queuePosition();
      });
      this.#styleObserver.observe(this.header, { attributes: true, attributeFilter: ['data-menu-style'] });
    }
    this.#queuePosition();
  }

  updatedCallback() {
    super.updatedCallback();
    this.#queuePosition();
    if (this.#activeKey && !this.#closing) {
      const trigger = this.querySelector('[data-aupiel-trigger="' + this.#activeKey + '"]');
      this.#activeKey = null;
      if (trigger) this.open(trigger);
      else this.close(false, true);
    }
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.removeEventListener('pointerover', this.#onPointerOver);
    this.removeEventListener('pointerout', this.#onPointerOut);
    this.removeEventListener('click', this.#onClick);
    this.removeEventListener('keydown', this.#onKeydown);
    this.removeEventListener('toggle', this.#onToggle, true);
    document.removeEventListener('keydown', this.#onEscape);
    document.removeEventListener('scroll', this.#queuePosition, true);
    window.removeEventListener('resize', this.#onResize);
    this.#reducedMotion.removeEventListener('change', this.#onMotionChange);
    this.#resizeObserver?.disconnect();
    this.#styleObserver?.disconnect();
    if (this.#frame !== null) cancelAnimationFrame(this.#frame);
    this.#frame = null;
    this.close(false, true);
  }

  #onResize = () => {
    setHeaderMenuStyle();
    if (window.innerWidth < 750) this.close(false, true);
    this.#queuePosition();
  };

  #onMotionChange = () => {
    if (!this.#reducedMotion.matches) return;
    if (this.#closing) this.close(false, true);
    else this.#animation?.cancel();
    this.#backdropAnimation?.cancel();
    this.#cancelPanels();
    this.#subAnimation?.cancel();
  };

  #queuePosition = () => {
    if (this.#frame !== null || !this.isConnected) return;
    this.#frame = requestAnimationFrame(() => {
      this.#frame = null;
      this.#layout();
      if (this.#activeKey) this.#position();
    });
  };

  #layout() {
    const header = this.header;
    const columns = header?.querySelector('.header__row--top .header__columns');
    const center = header?.querySelector('.header__column--center');
    const list = this.querySelector('.aupiel-nav-list');
    if (!columns || !center || !list || header.dataset.menuStyle === 'drawer' || window.innerWidth < 750) return;
    const cs = getComputedStyle(columns);
    const gap = parseFloat(getComputedStyle(list).columnGap) || 0;
    const width = [...list.children].reduce((total, item) => total + item.getBoundingClientRect().width, 0) + Math.max(0, list.children.length - 1) * gap;
    const available = (columns.clientWidth - parseFloat(cs.paddingLeft) - parseFloat(cs.paddingRight) - center.getBoundingClientRect().width - 2 * parseFloat(cs.columnGap)) / 2;
    const layout = width > available + 1 ? 'stacked' : 'inline';
    if (header.dataset.aupielLayout !== layout) header.dataset.aupielLayout = layout;
  }

  #position() {
    const rect = this.header?.getBoundingClientRect();
    if (!rect) return;
    for (const layer of [this.drawer, this.backdrop, this.secondary]) {
      layer?.style.setProperty('--aupiel-drawer-top', Math.max(0, rect.bottom) + 'px');
    }
  }

  #onToggle = (event) => {
    if (event.target === this.drawer && event.newState === 'closed' && !this.drawer.matches(':popover-open')) {
      this.close(false, true);
    }
  };

  #triggerFrom(event) {
    if (!(event.target instanceof Element)) return null;
    const trigger = event.target.closest('[data-aupiel-trigger], [data-aupiel-sub-trigger]');
    return trigger instanceof HTMLElement && this.contains(trigger) ? trigger : null;
  }

  #onPointerOver = (event) => {
    if (event.pointerType === 'touch') return;
    const trigger = this.#triggerFrom(event);
    if (!trigger && event.target instanceof Element && event.target.closest('[data-aupiel-leaf]')) this.closeSecondary();
    if (!trigger || (event.relatedTarget instanceof Node && trigger.contains(event.relatedTarget))) return;
    clearTimeout(this.#hoverTimer);
    this.#hoverTimer = setTimeout(() => {
      if (trigger.isConnected && trigger.matches(':hover')) {
        if (trigger.hasAttribute('data-aupiel-sub-trigger')) this.openSecondary(trigger);
        else this.open(trigger);
      }
    }, Number(this.dataset.hoverDelay) || 0);
  };

  #onPointerOut = (event) => {
    const trigger = this.#triggerFrom(event);
    if (trigger && !(event.relatedTarget instanceof Node && trigger.contains(event.relatedTarget))) clearTimeout(this.#hoverTimer);
  };

  #onClick = (event) => {
    if (!(event.target instanceof Element)) return;
    if (event.target.closest('[data-aupiel-sub-close]')) { this.closeSecondary(true); return; }
    if (event.target.closest('[data-aupiel-close]')) { this.close(true); return; }
    const trigger = this.#triggerFrom(event);
    if (trigger) { event.preventDefault();
      if (trigger.hasAttribute('data-aupiel-sub-trigger')) this.openSecondary(trigger);
      else this.open(trigger);
    }
  };

  #onKeydown = (event) => {
    if (event.key === 'ArrowLeft' && this.secondary?.contains(event.target) && this.#subKey) {
      event.preventDefault(); this.closeSecondary(true); return;
    }
    const trigger = this.#triggerFrom(event);
    if (!trigger) return;
    if (trigger.hasAttribute('data-aupiel-sub-trigger') && ['ArrowRight', 'ArrowDown'].includes(event.key)) {
      event.preventDefault(); this.openSecondary(trigger);
      this.querySelector('[data-aupiel-sub-panel="' + this.#subKey + '"] a')?.focus({ preventScroll: true });
    } else if (event.key === 'ArrowDown') {
      event.preventDefault(); this.open(trigger);
      this.querySelector('[data-aupiel-panel="' + trigger.dataset.aupielTrigger + '"] :is(a,button)')?.focus({ preventScroll: true });
    }
  };

  #onEscape = (event) => {
    if (event.key !== 'Escape' || !this.#activeKey) return;
    event.preventDefault();
    if (this.#subKey) this.closeSecondary(true);
    else this.close(true);
  };

  openSecondary(trigger) {
    const key = trigger.dataset.aupielSubTrigger;
    const panel = this.querySelector('[data-aupiel-sub-panel="' + key + '"]');
    const drawer = this.secondary;
    if (!this.#activeKey || this.#closing || !panel || !drawer || this.#subKey === key) return;
    this.#subAnimation?.cancel();
    const wasOpen = drawer.matches(':popover-open');
    for (const candidate of this.querySelectorAll('[data-aupiel-sub-panel]')) candidate.hidden = candidate !== panel;
    for (const button of this.querySelectorAll('[data-aupiel-sub-trigger]')) button.setAttribute('aria-expanded', String(button === trigger));
    this.#subKey = key;
    this.#position();
    if (!wasOpen) drawer.showPopover();
    if (this.duration) this.#subAnimation = (wasOpen ? panel : drawer).animate([
      { opacity:0, transform:wasOpen ? 'translateY(8px)' : 'translateX(-24px)' },
      { opacity:1, transform:'translate(0)' }
    ], { duration:this.duration, easing:'cubic-bezier(.22,1,.36,1)' });
    drawer.querySelector('.aupiel-drawer__body').scrollTop = 0;
  }

  closeSecondary(restoreFocus = false) {
    const trigger = this.querySelector('[data-aupiel-sub-trigger="' + this.#subKey + '"]');
    if (restoreFocus) trigger?.focus({ preventScroll:true });
    this.#subAnimation?.cancel();
    if (this.secondary?.matches(':popover-open')) this.secondary.hidePopover();
    this.#subKey = null;
    for (const button of this.querySelectorAll('[data-aupiel-sub-trigger]')) button.setAttribute('aria-expanded','false');
    for (const panel of this.querySelectorAll('[data-aupiel-sub-panel]')) panel.hidden = true;
  }

  #cancelPanels() {
    for (const animation of this.#panelAnimations) animation.cancel();
    this.#panelAnimations = [];
  }

  open(trigger) {
    clearTimeout(this.#hoverTimer);
    const key = trigger.dataset.aupielTrigger;
    const panel = this.querySelector('[data-aupiel-panel="' + key + '"]');
    const drawer = this.drawer;
    if (!key || !panel || !drawer || !this.isConnected || window.innerWidth < 750 || this.header?.dataset.menuStyle === 'drawer') return;
    if (this.#activeKey === key && !this.#closing && drawer.matches(':popover-open') && !panel.hidden) return;
    const wasOpen = drawer.matches(':popover-open');
    const wasClosing = this.#closing;
    const current = wasClosing ? getComputedStyle(drawer) : null;
    const from = current ? { opacity: current.opacity, transform: current.transform } : null;
    this.#closing = false;
    this.#animation?.cancel();
    this.#cancelPanels();
    if (this.secondary?.contains(document.activeElement)) trigger.focus({ preventScroll: true });
    this.closeSecondary();
    this.#activeKey = key;
    this.#lastTrigger = trigger;
    const previousPanel = this.querySelector('[data-aupiel-panel]:not([hidden])');
    if (previousPanel && previousPanel !== panel && previousPanel.contains(document.activeElement)) trigger.focus({ preventScroll: true });
    for (const candidate of this.querySelectorAll('[data-aupiel-panel]')) candidate.hidden = candidate !== panel;
    for (const button of this.querySelectorAll('[data-aupiel-trigger]')) button.setAttribute('aria-expanded', String(button.dataset.aupielTrigger === key));
    this.setAttribute('aria-expanded', 'true');
    lockScroll(this);
    this.#position();
    const backdrop = this.backdrop;
    if (backdrop && (!wasOpen || wasClosing || !backdrop.matches(':popover-open'))) {
      const opacity = backdrop.matches(':popover-open') ? getComputedStyle(backdrop).opacity : 0;
      this.#backdropAnimation?.cancel();
      if (!backdrop.matches(':popover-open')) backdrop.showPopover();
      if (this.duration) this.#backdropAnimation = backdrop.animate([{ opacity }, { opacity: 1 }], { duration: this.duration, easing: 'ease-out' });
    }
    if (!wasOpen) drawer.showPopover();
    if ((!wasOpen || wasClosing) && this.duration) {
      this.#animation = drawer.animate([
        from || { opacity: 0, transform: 'translateX(-32px)' },
        { opacity: 1, transform: 'translateX(0)' }
      ], { duration: this.duration, easing: 'cubic-bezier(.22,1,.36,1)' });
    }
    if (this.duration) {
      this.#panelAnimations = [...panel.querySelectorAll('.aupiel-drawer__links > li')].map((item, index) => item.animate([
        { opacity: 0, transform: 'translateY(10px)' }, { opacity: 1, transform: 'translateY(0)' }
      ], { duration: this.duration * .75, delay: Math.min(index * 30, 150), easing: 'cubic-bezier(.22,1,.36,1)', fill: 'backwards' }));
    }
    const body = drawer.querySelector('.aupiel-drawer__body');
    if (body) body.scrollTop = 0;
  }

  close(restoreFocus = false, immediate = false) {
    clearTimeout(this.#hoverTimer);
    const drawer = this.drawer;
    if (this.#closing && !immediate) return;
    this.closeSecondary();
    this.#cancelPanels();
    this.#animation?.cancel();
    const backdrop = this.backdrop;
    const backdropOpacity = backdrop ? getComputedStyle(backdrop).opacity : 1;
    this.#backdropAnimation?.cancel();
    if (restoreFocus && this.#lastTrigger?.isConnected) this.#lastTrigger.focus({ preventScroll: true });
    this.#closing = true;
    const finish = () => {
      if (drawer?.matches(':popover-open')) drawer.hidePopover();
      if (backdrop?.matches(':popover-open')) backdrop.hidePopover();
      this.#backdropAnimation?.cancel();
      unlockScroll(this);
      this.#activeKey = null;
      this.#closing = false;
      this.setAttribute('aria-expanded', 'false');
      for (const button of this.querySelectorAll('[data-aupiel-trigger]')) button.setAttribute('aria-expanded', 'false');
      for (const panel of this.querySelectorAll('[data-aupiel-panel]')) panel.hidden = true;
    };
    if (immediate || !this.duration || !drawer?.matches(':popover-open') || !drawer.isConnected) { finish(); return; }
    if (backdrop?.matches(':popover-open')) {
      this.#backdropAnimation = backdrop.animate([{ opacity: backdropOpacity }, { opacity: 0 }], { duration: this.duration * .6, easing: 'ease-in', fill: 'forwards' });
    }
    const animation = drawer.animate([
      { opacity: 1, transform: 'translateX(0)' }, { opacity: 0, transform: 'translateX(-24px)' }
    ], { duration: this.duration * .6, easing: 'cubic-bezier(.4,0,1,1)', fill: 'forwards' });
    this.#animation = animation;
    animation.finished.then(() => {
      if (this.#animation === animation && this.#closing) { finish(); animation.cancel(); }
    }).catch(() => {});
  }
}

if (!customElements.get('header-menu')) customElements.define('header-menu', HeaderMenu);
